import { ShoppingCart, Wallet } from 'lucide-react';

/**
 * DualWalletCards — display-only 3-tile balance row for the Dashboard.
 *
 * Per the "Dashboard = fruits of labor" UX principle, this component shows
 * balances only. No Buy Position / Buy Sub Pack / Withdrawal Request
 * buttons, no pool-status polling, no modals. The buy actions live on the
 * Purchase / Deposit subpage; the withdraw action lives on the Withdrawal
 * subpage.
 *
 * Pure-display + no internal polling = zero contribution to the per-IP
 * sliding-window rate limit.
 *
 * Auto-repurchase is retired (D-28, owner request 2026-09-22): its reserve
 * card and the divert-% read are removed, not just hidden. Balances moved to
 * the withdrawal balance at import.
 */
const DualWalletCards = ({ me }) => {
  if (!me) return null;

  return (
    <section className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="dual-wallet">
      {/* Purchase Balance */}
      <div className="sc-glass rounded-2xl p-5 md:p-6 relative overflow-hidden" data-testid="card-purchase">
        <div
          className="absolute -top-16 -right-16 w-44 h-44 pointer-events-none rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255,182,18,0.18), transparent 60%)',
          }}
          aria-hidden="true"
        />
        <div className="relative z-10 flex items-start justify-between">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center border"
            style={{
              background: 'rgba(255,182,18,0.06)',
              borderColor: 'rgba(255,182,18,0.28)',
            }}
          >
            <ShoppingCart className="w-5 h-5 text-[#FFC20E]" />
          </div>
        </div>
        <div className="relative z-10 mt-5">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Purchase Balance</div>
          <div className="sc-tabular font-display font-bold text-3xl md:text-4xl mt-2 leading-none sc-gold-text" data-testid="purchase-balance-value">
            ${Number(me.purchase_balance || 0).toFixed(2)}
          </div>
          <p className="mt-2 text-xs text-[var(--sc-text-dim)] leading-snug">Available capital for securing positions and Sub Packs.</p>
        </div>
      </div>

      {/* Withdrawal Balance */}
      <div className="sc-glass rounded-2xl p-5 md:p-6 relative overflow-hidden" data-testid="card-withdrawal">
        <div
          className="absolute -top-16 -right-16 w-44 h-44 pointer-events-none rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255,194,14,0.18), transparent 60%)',
          }}
          aria-hidden="true"
        />
        <div className="relative z-10 flex items-start justify-between">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center border"
            style={{
              background: 'rgba(255,182,18,0.06)',
              borderColor: 'rgba(255,182,18,0.28)',
            }}
          >
            <Wallet className="w-5 h-5 text-[#FFC20E]" />
          </div>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Spendable Income</span>
        </div>
        <div className="relative z-10 mt-5">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Withdrawal Balance</div>
          <div className="sc-tabular font-display font-bold text-3xl md:text-4xl mt-2 leading-none sc-gold-text" data-testid="withdrawal-balance-value">
            ${Number(me.withdrawal_balance || 0).toFixed(2)}
          </div>
          <p className="mt-2 text-xs text-[var(--sc-text-dim)] leading-snug">Cycle payouts &amp; matching bonuses. Eligible for crypto payout.</p>
        </div>
      </div>
    </section>
  );
};

export default DualWalletCards;
